import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { SellerAccountDetailItem } from '@shared/api/needAccount/needAccount.interface';
import {
  sellerAccountDetailExportUrl,
  sellerMonthlyAccountedUrl,
  sellerNeedAccountUrl,
} from '@shared/api/needAccount/sellerAccount.hook';
import { accountShopOrderList } from '@shared/api/shopOrder/shopOrder.controller';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Checkbox, DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';

import {
  buildBrandFilterParams,
  sellerAccountUserIdAtom,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from './accountContent.atom';

//* Admin이 해당 Seller에게 돈을 지급한 후 정산 버튼을 눌러 isAccounted true 및 accountDate를 설정하기 위함

interface Props {
  year: number;
  month: number;
}

export const SellerAccountButton: FC<Props> = function SellerAccountButton({
  year,
  month,
}: Props) {
  const openId = `sellerAccountButton-${year}-${month}`;

  const { sellerRole, isAdmin } = useAuth();
  const role = sellerRole();
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  // 선택된 bugoBrand → API 파라미터 변환
  const selectedBrandIds = useRecoilValue(sellerSelectedBrandIdsAtom);
  const brandOptions = useRecoilValue(sellerBrandOptionsAtom);
  const userId = useRecoilValue(sellerAccountUserIdAtom);
  const brandFilter = useMemo(
    () => buildBrandFilterParams(selectedBrandIds, brandOptions),
    [selectedBrandIds, brandOptions],
  );

  // ─── 상태 ───
  const [detailItems, setDetailItems] = useState<SellerAccountDetailItem[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [accountedAsEtc, setAccountedAsEtc] = useState(false);

  // ─── 계산 ───
  const totalCount = detailItems.length;
  const accountedCount = useMemo(
    () => detailItems.filter((i) => i.isAccounted).length,
    [detailItems],
  );
  const pendingCount = totalCount - accountedCount;
  const pendingIds = useMemo(
    () => detailItems.filter((i) => !i.isAccounted).map((i) => i._id),
    [detailItems],
  );

  const refetchAccountQueries = useCallback(() => {
    queryClient.invalidateQueries(sellerMonthlyAccountedUrl(sellerRole(), year, userId));
    queryClient.invalidateQueries(sellerNeedAccountUrl(sellerRole(), userId));
  }, [queryClient, sellerRole, userId, year]);

  // ─── 모달 열기: 데이터 fetch ───
  const handleOpen = useCallback(async () => {
    setProcessing(false);
    setDetailItems([]);
    setOpen(true);
    setFetchLoading(true);

    try {
      const url = sellerAccountDetailExportUrl(
        role,
        year,
        month,
        'need-account',
        brandFilter,
        userId,
      );
      const { data } = await api_.get(url);
      const items: SellerAccountDetailItem[] = Array.isArray(data)
        ? data
        : data.data ?? [];
      setDetailItems(items);
    } catch (err) {
      console.error('정산 데이터 불러오기 실패', err);
    } finally {
      setFetchLoading(false);
    }
  }, [role, year, month, brandFilter, userId, setOpen]);

  // ─── 정산 처리 ───
  const handleAccount = useCallback(async () => {
    if (pendingIds.length === 0) return;
    setProcessing(true);
    try {
      await accountShopOrderList(pendingIds, selectedDate.toDate(), {
        accountedAsEtc,
      });
      notificateSuccess(notifiacationInstance, `${pendingIds.length}건 정산 완료`);
      refetchAccountQueries();
      setOpen(false);
    } catch (err) {
      console.error('정산 처리 실패', err);
      notificateError(notifiacationInstance, '정산 처리 실패');
    } finally {
      setProcessing(false);
    }
  }, [
    pendingIds,
    selectedDate,
    accountedAsEtc,
    notifiacationInstance,
    setOpen,
    refetchAccountQueries,
  ]);

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={!isAdmin()}
        className="whitespace-pre shadow-sm theme-text-4 theme-bg-main"
      >
        정산 처리
      </Button>

      <Modal openId={openId}>
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-4 py-2">
          {fetchLoading ? (
            <div className="flex flex-col items-center gap-3">
              <Spin size="large" />
              <p className="text-sm text-gray-500">데이터를 불러오는 중...</p>
            </div>
          ) : (
            <>
              <p className="text-base font-bold">
                {year}년 {month}월 정산
              </p>

              {/* 건수 현황 */}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500">
                  전체 <span className="font-medium text-gray-800">{totalCount}</span>건
                </span>
                <span className="text-green-600">
                  완료 <span className="font-medium">{accountedCount}</span>건
                </span>
                {pendingCount > 0 && (
                  <span className="text-orange-500">
                    미정산 <span className="font-bold">{pendingCount}</span>건
                  </span>
                )}
              </div>

              {/* 정산일 선택 */}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-sm text-gray-600">정산일:</span>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  allowClear={false}
                />
              </div>

              {/* 기타정산 체크 */}
              <Checkbox
                checked={accountedAsEtc}
                onChange={(e) => setAccountedAsEtc(e.target.checked)}
              >
                <span className="text-sm">기타정산</span>
              </Checkbox>

              {/* 정산 버튼 */}
              <button
                className="rounded-md bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={processing || pendingCount === 0}
                onClick={handleAccount}
              >
                {processing ? '처리중...' : `미정산 ${pendingCount}건 정산처리`}
              </button>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};
